import { useCallback, useEffect, useRef, useState } from "react";
import {
	getAllPersonAssignments,
	type PersonAssignmentRetrieveType,
} from "../../../api/assignments/person-assignment";
import { getCommentsByObject } from "../../../api/comments/comments";
import { getCooldowns } from "../../../api/cooldown";
import {
	getEmailQueueForPerson,
	getLinkedinMessageQueueForPerson,
} from "../../../api/messaging";
import { getPersonActivities } from "../../../api/person/activity";
import { getPersonConversationsById } from "../../../api/person/conversations";
import { getPersonEmailsById } from "../../../api/person/emails";
import { getPersonEventsById } from "../../../api/person/events";
import { getAttachments } from "../../../api/person/person-attachment";
import { getPersonVotesById } from "../../../api/votes/votes";
import type { ActivityEntry } from "./timeline-types";

interface UseTimelineDataResult {
	entries: ActivityEntry[];
	loading: boolean;
	error: string | null;
	refetch: () => void;
}

/**
 * Custom hook to fetch and transform all timeline data for a person
 */
export function useTimelineData(personId: number): UseTimelineDataResult {
	const isMounted = useRef(true);
	const [entries, setEntries] = useState<ActivityEntry[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [refetchCounter, setRefetchCounter] = useState(0);

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchAllData = async () => {
			setLoading(true);
			setError(null);
			setEntries([]);

			try {
				const [
					emailQueue,
					linkedinQueue,
					conversations,
					emailJournal,
					votes,
					events,
					boardMovements,
					comments,
					attachments,
					activities,
					cooldowns,
				] = await Promise.all([
					getEmailQueueForPerson(personId, { includeSent: true }),
					getLinkedinMessageQueueForPerson(personId, { includeSent: true }),
					getPersonConversationsById(personId).then((res) => res.results),
					getPersonEmailsById(personId).then((res) => res.results),
					getPersonVotesById(personId).then((res) => res.results),
					getPersonEventsById(personId),
					getAllPersonAssignments({ personId }).then((res) => res.results),
					getCommentsByObject("persons", personId),
					getAttachments({ person: personId, deleted: false }).then(
						(res) => res.results
					),
					getPersonActivities(personId),
					getCooldowns({ person: personId, hideHistory: false }).then(
						(res) => res.results
					),
				]);

				if (!isMounted.current) return;

				const combined: ActivityEntry[] = [];

				// Collect email_journal_ids from sent pitches to deduplicate
				const pitchEmailJournalIds = new Set<number>();
				for (const eq of emailQueue) {
					if (eq.sent && eq.email_journal_id) {
						pitchEmailJournalIds.add(eq.email_journal_id);
					}
				}

				// Collect cc_ids from sent LinkedIn pitches to deduplicate
				const pitchConversationContentIds = new Set<number>();
				const ccIdToContentDate = new Map<number, string>();
				for (const lq of linkedinQueue) {
					if (lq.sent && lq.cc_id) {
						pitchConversationContentIds.add(lq.cc_id);
					}
				}
				// Build cc_id to date map from conversation contents
				for (const conv of conversations) {
					for (const content of conv.contents || []) {
						if (pitchConversationContentIds.has(content.id)) {
							ccIdToContentDate.set(
								content.id,
								content.created_at || content.created
							);
						}
					}
				}

				// Email Queue entries - distinguish between queued and sent
				for (const eq of emailQueue) {
					if (eq.sent) {
						combined.push({
							type: "email_sent",
							date: eq.sent_at || eq.created,
							data: eq,
						});
					} else {
						combined.push({
							type: "email_queued",
							date: eq.created,
							data: eq,
						});
					}
				}

				// LinkedIn Queue entries - distinguish between queued and sent
				for (const lq of linkedinQueue) {
					if (lq.sent) {
						const contentDate = lq.cc_id
							? ccIdToContentDate.get(lq.cc_id)
							: undefined;
						combined.push({
							type: "linkedin_sent",
							date:
								contentDate ||
								lq.updated ||
								lq.created ||
								new Date().toISOString(),
							data: lq,
						});
					} else {
						combined.push({
							type: "linkedin_queued",
							date: lq.created || new Date().toISOString(),
							data: lq,
						});
					}
				}

				// LinkedIn Conversations - flatten and determine direction
				for (const conv of conversations) {
					for (const content of conv.contents || []) {
						if (pitchConversationContentIds.has(content.id)) continue;
						const direction = content.type === "S" ? "outbound" : "inbound";
						combined.push({
							type: "linkedin_conversation",
							date: content.created_at || content.created,
							direction,
							conversationUsername: conv.username,
							conversationUserFirstName: conv.user_first_name,
							conversationShortUrn: conv.short_urn,
							conversationType: conv.conversation_type_str,
							personName: conv.person_name,
							data: content,
						});
					}
				}

				// Email Journal entries - deduplicate by message_id
				const sortedEmails = [...emailJournal].sort((a, b) => {
					const methodDiff = (b.email_method || 0) - (a.email_method || 0);
					if (methodDiff !== 0) return methodDiff;
					return new Date(b.date).getTime() - new Date(a.date).getTime();
				});
				const seenMessageIds = new Map<string, (typeof emailJournal)[0]>();
				for (const ej of sortedEmails) {
					const key = ej.message_id ? String(ej.message_id) : String(ej.id);
					if (!seenMessageIds.has(key)) seenMessageIds.set(key, ej);
				}
				for (const ej of seenMessageIds.values()) {
					if (pitchEmailJournalIds.has(ej.email_journal_id)) continue;
					const direction = ej.folder_type === 1 ? "outbound" : "inbound";
					combined.push({
						type: "email_journal",
						date: ej.date || ej.created,
						direction,
						data: ej,
					});
				}

				// Votes
				for (const vote of votes) {
					combined.push({ type: "vote", date: vote.created, data: vote });
				}

				// Events - filter out ThumbUp/ThumbDown which are votes.
				// Connections sort on the real LinkedIn connected date, falling
				// back to discovery time (timestamp=created) when it's unknown.
				for (const event of events) {
					if (event.icon !== "ThumbDown" && event.icon !== "ThumbUp") {
						const isConnection = event.model === "Connection";
						combined.push({
							type: "event",
							date:
								isConnection && event.connected
									? event.connected
									: new Date(event.timestamp).toISOString(),
							data: event,
						});
					}
				}

				// Board movements - group by assignment and calculate FROM column
				const movementsByAssignment = new Map<
					number,
					PersonAssignmentRetrieveType[]
				>();
				for (const pa of boardMovements) {
					if (!movementsByAssignment.has(pa.assignment)) {
						movementsByAssignment.set(pa.assignment, []);
					}
					movementsByAssignment.get(pa.assignment)!.push(pa);
				}
				for (const movements of movementsByAssignment.values()) {
					movements.sort(
						(a, b) =>
							new Date(a.created).getTime() -
							new Date(b.created).getTime()
					);
					for (let i = 0; i < movements.length; i++) {
						const pa = movements[i];
						const fromColumn = i > 0 ? movements[i - 1].column : null;
						combined.push({
							type: "board_movement",
							date: pa.created,
							data: pa,
							fromColumn,
						});
					}
				}

				// Notes (comments)
				for (const comment of comments) {
					combined.push({
						type: "note",
						date: comment.submit_date,
						data: comment,
					});
				}

				// Attachments
				for (const attachment of attachments) {
					if (attachment.created) {
						combined.push({
							type: "attachment",
							date: attachment.created,
							data: attachment,
						});
					}
				}

				// Manual activities (calls and meetings)
				for (const activity of activities) {
					combined.push({
						type: activity.activity_type as "call" | "meeting",
						date: activity.happened_at,
						data: activity,
					});
				}

				// Cooldowns
				for (const cooldown of cooldowns) {
					if (cooldown.created) {
						combined.push({
							type: "cooldown",
							date: cooldown.created,
							data: cooldown,
						});
					}
				}

				// Sort by date descending (newest first)
				combined.sort(
					(a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
				);
				setEntries(combined);
			} catch (err) {
				if (isMounted.current) {
					setError(
						err instanceof Error ? err.message : "Failed to load activity"
					);
				}
			} finally {
				if (isMounted.current) {
					setLoading(false);
				}
			}
		};

		fetchAllData();
	}, [personId, refetchCounter]);

	const refetch = useCallback(() => {
		setRefetchCounter((c) => c + 1);
	}, []);

	return { entries, loading, error, refetch };
}
